import { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography, alpha } from '@mui/material';
import { DeleteOutlineRounded, HideImageOutlined, PhotoLibraryOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { Util } from '../../util/Util';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogContent,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from '../MoleDialogShell';
import { Z_INDEX } from '../../styles/zIndex';
import LineImagePicker from './LineImagePicker';
import { DocImageKind, LineImage, LineImageOrigin, docImageSrc, fetchLineImages, saveLineImages } from './docImagesApi';

const ORIGIN_COLOR: Record<LineImageOrigin, 'default' | 'primary' | 'secondary' | 'success' | 'warning'> = {
	SAVED: 'success',
	OFFER: 'secondary',
	CATALOG: 'default',
	NONE: 'warning',
	EDITED: 'primary',
};

interface Props {
	open: boolean;
	kind: DocImageKind;
	batch: string;
	onClose: () => void;
	/** Called once image review is approved; the caller then exports. */
	onApproved: () => void;
	/** Read-only users may export the current images but cannot change them. */
	canEdit?: boolean;
}

/** Export-time check of what each line prints; editable users may freeze choices. */
const LineImagesReview = ({
	open,
	kind,
	batch,
	onClose,
	onApproved,
	canEdit = false,
}: Props) => {
	const { t } = useTranslation();
	const { api } = useAuth();
	const themeColor = useMoleThemeColor();
	const [lines, setLines] = useState<LineImage[] | null>(null);
	const [picking, setPicking] = useState<number | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) return;
		setLines(null);
		let cancelled = false;
		const load = async () => {
			try {
				const res = await fetchLineImages(api, kind, batch);
				if (cancelled) return;
				if (res?.status === 200) {
					setLines(res.data.lines);
				} else {
					toast.error(t('common:docImages.loadFailed'));
					onClose();
				}
			} catch {
				if (!cancelled) {
					toast.error(t('common:docImages.loadFailed'));
					onClose();
				}
			}
		};
		void load();
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, kind, batch, api]);

	const updateLine = (index: number, patch: Partial<LineImage>) =>
		setLines((current) => current && current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

	const handleApprove = async () => {
		if (!lines) return;
		if (!canEdit) {
			onApproved();
			return;
		}
		setSaving(true);
		try {
			const res = await saveLineImages(api, kind, batch, lines);
			if (res?.status === 200) {
				onApproved();
			} else {
				toast.error(t('common:docImages.saveFailed'));
			}
		} catch {
			toast.error(t('common:docImages.saveFailed'));
		} finally {
			setSaving(false);
		}
	};

	const picked = picking !== null && lines ? lines[picking] : null;

	return (
		<>
			<MoleDialog
				open={open}
				onClose={saving ? undefined : onClose}
				maxWidth="md"
				fullWidth
				themeColor={themeColor}
				sx={{ zIndex: Z_INDEX.nestedDialog }}
			>
				<MoleDialogHeader
					title={t('common:docImages.reviewTitle')}
					subtitle={t('common:docImages.reviewSubtitle')}
					icon={<PhotoLibraryOutlined />}
					onClose={saving ? undefined : onClose}
					themeColor={themeColor}
				/>
				<MoleDialogContent>
					{lines === null ? (
						<Stack alignItems="center" sx={{ py: 6 }}>
							<CircularProgress sx={{ color: themeColor }} />
						</Stack>
					) : (
						<Stack spacing={1}>
							{lines.map((line, index) => (
								<Stack
									key={`${line.item}-${line.amount}-${line.item_desc}`}
									direction="row"
									spacing={1.5}
									alignItems="center"
									sx={{ p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
								>
									<Box
										sx={{
											width: 72,
											height: 72,
											flexShrink: 0,
											borderRadius: 1.5,
											overflow: 'hidden',
											border: '1px solid',
											borderColor: 'divider',
											bgcolor: alpha(themeColor, 0.04),
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											'& img': { width: '100%', height: '100%', objectFit: 'contain' },
										}}
									>
										{line.url && line.origin !== 'NONE' ? (
											<img src={docImageSrc(line.url)} alt="" loading="lazy" />
										) : (
											<HideImageOutlined sx={{ color: 'text.disabled' }} />
										)}
									</Box>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography fontWeight={700} noWrap title={line.item_desc}>{line.item_desc}</Typography>
										<Typography variant="body2" color="text.secondary">{Util.formatNumber(line.amount)}</Typography>
										<Chip
											label={t(`common:docImages.origin.${line.origin}`)}
											color={ORIGIN_COLOR[line.origin]}
											size="small"
											variant={line.origin === 'CATALOG' ? 'outlined' : 'filled'}
											sx={{ mt: 0.5, height: 20, fontSize: 11, fontWeight: 700 }}
										/>
									</Box>
									{canEdit && (
										<>
											<Button
												variant="outlined"
												size="small"
												onClick={() => setPicking(index)}
												disabled={saving}
												sx={{ color: themeColor, borderColor: themeColor, flexShrink: 0 }}
											>
												{t('common:docImages.change')}
											</Button>
											<Tooltip title={t('common:docImages.remove')}>
												<span>
													<IconButton
														onClick={() => updateLine(index, { image: null, url: null, origin: 'NONE' })}
														disabled={saving || line.origin === 'NONE'}
													>
														<DeleteOutlineRounded />
													</IconButton>
												</span>
											</Tooltip>
										</>
									)}
								</Stack>
							))}
						</Stack>
					)}
				</MoleDialogContent>
				<MoleDialogActions>
					<MoleCancelButton onClick={onClose} disabled={saving}>{t('common:ui.buttons.cancel')}</MoleCancelButton>
					<MolePrimaryButton
						onClick={() => void handleApprove()}
						disabled={!lines || saving}
						startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
					>
						{t('common:docImages.approveExport')}
					</MolePrimaryButton>
				</MoleDialogActions>
			</MoleDialog>
			{picked && (
				<LineImagePicker
					open
					kind={kind}
					itemId={picked.item}
					itemName={picked.item_desc}
					onClose={() => setPicking(null)}
					onPicked={({ image, url }) => {
						updateLine(picking as number, { image, url, origin: 'EDITED' });
						setPicking(null);
					}}
				/>
			)}
		</>
	);
};

export default LineImagesReview;
